import { useState, useEffect } from 'react';
import {
  Coins, CheckCircle2, X, Building2, ExternalLink, AlertCircle
} from 'lucide-react';
import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import Skeleton from '../../components/ui/Skeleton';
import { useShop } from '../../hooks/useShop';
import { useAuth } from '../../contexts/AuthContext';
import { api, tokenStorage } from '../../api/client';

const STATUS_TONES = {
  PENDING_RETURN: 'action',
  CORE_RETURNED: 'insight',
  CREDIT_RECEIVED: 'success',
  VOID: 'neutral',
};

const STATUS_LABELS = {
  PENDING_RETURN: 'Awaiting return',
  CORE_RETURNED: 'Returned (waiting credit)',
  CREDIT_RECEIVED: 'Credit received',
  VOID: 'Void',
};

const TABS = [
  { key: 'open', label: 'Open', filter: 'PENDING_RETURN,CORE_RETURNED' },
  { key: 'closed', label: 'Closed', filter: 'CREDIT_RECEIVED,VOID' },
  { key: 'all', label: 'All', filter: null },
];

export default function CoreChargesPage() {
  const { currentShopId, currentShop, shops } = useShop();
  const { isAllShops } = useAuth();
  const [activeTab, setActiveTab] = useState('open');
  const [shopFilter, setShopFilter] = useState(isAllShops ? '' : (currentShopId || ''));
  const effectiveShopId = shopFilter || (isAllShops ? null : currentShopId);
  const [cores, setCores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [returningCore, setReturningCore] = useState(null);
  const [confirmingCredit, setConfirmingCredit] = useState(null);
  const [voidingCore, setVoidingCore] = useState(null);

  const filterStatus = TABS.find((t) => t.key === activeTab)?.filter;

  async function refresh() {
    if (!tokenStorage.getAccess()) return;
    setLoading(true);
    try {
      const [data, sum] = await Promise.all([
        api.listCoreCharges(effectiveShopId, filterStatus),
        api.coreChargeSummary(effectiveShopId),
      ]);
      setCores(data.cores || []);
      setSummary(sum);
    } catch {
      setCores([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, [activeTab, effectiveShopId]);

  return (
    <div>
      <PageHeader
        title="Core Charges"
        description={`${shopFilter ? (shops.find(s => s.shop_id === Number(shopFilter))?.shop_nickname || 'Shop') : 'All shops'} · Cores we paid for, awaiting physical return + credit.`}
        meta={
          summary && (
            <div className="flex gap-2 flex-wrap">
              {Object.entries(summary.by_status || {}).map(([status, count]) => (
                <Badge key={status} tone={STATUS_TONES[status]} size="sm">
                  {STATUS_LABELS[status] || status}: {count}
                </Badge>
              ))}
              {summary.pending_dollars_usd > 0 && (
                <Badge tone="action" size="sm">
                  ${summary.pending_dollars_usd.toFixed(0)} outstanding
                </Badge>
              )}
            </div>
          )
        }
      />

      {isAllShops && shops.length > 0 && (
        <div className="flex items-center gap-3 mb-4 text-[13px]">
          <span className="text-[var(--color-warm-500)] font-semibold uppercase text-[10px] tracking-wide">Shop:</span>
          <select
            value={shopFilter}
            onChange={(e) => setShopFilter(e.target.value)}
            className="h-9 px-3 bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] text-[13px] font-semibold"
          >
            <option value="">All shops</option>
            {shops.map((s) => (
              <option key={s.shop_id} value={s.shop_id}>
                {s.shop_nickname || s.shop_name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="flex gap-1 mb-4 border-b border-[var(--color-warm-100)]">
        {TABS.map((t) => {
          const isActive = activeTab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 h-10 text-[13px] font-semibold border-b-2 transition-colors -mb-px ${
                isActive
                  ? 'border-[var(--color-warm-900)] text-[var(--color-warm-900)]'
                  : 'border-transparent text-[var(--color-warm-500)] hover:text-[var(--color-warm-800)]'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} height={70} />)}</div>
      ) : cores.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <Coins size={36} className="text-[var(--color-warm-400)] mx-auto mb-2" />
            <div className="text-[14px] font-semibold text-[var(--color-warm-900)]">
              {activeTab === 'open' ? 'No open core charges' : 'No core charges in this view'}
            </div>
            <div className="text-[12px] text-[var(--color-warm-500)] mt-1">
              Cores get tracked when a check is paid INCLUSIVE of the core deposit (vs less the core).
            </div>
          </div>
        </Card>
      ) : (
        <Card padded={false}>
          <ul className="divide-y divide-[var(--color-warm-100)]">
            {cores.map((c) => (
              <CoreRow
                key={c.id}
                core={c}
                onMarkReturned={() => setReturningCore(c)}
                onConfirmCredit={() => setConfirmingCredit(c)}
                onVoid={() => setVoidingCore(c)}
              />
            ))}
          </ul>
        </Card>
      )}

      {returningCore && (
        <SimpleNotesModal
          title={`Mark core returned — $${returningCore.core_amount_usd.toFixed(2)}`}
          description="Confirm the physical core was returned to the vendor. Next stage: waiting for the credit to land on the next statement / next payment."
          confirmLabel="Mark returned"
          onClose={() => setReturningCore(null)}
          onConfirm={async (notes) => {
            await api.markCoreReturned(returningCore.id, notes);
            setReturningCore(null);
            refresh();
          }}
        />
      )}
      {confirmingCredit && (
        <CreditModal
          core={confirmingCredit}
          onClose={() => setConfirmingCredit(null)}
          onConfirmed={async (ref, notes) => {
            await api.markCoreCreditReceived(confirmingCredit.id, ref, notes);
            setConfirmingCredit(null);
            refresh();
          }}
        />
      )}
      {voidingCore && (
        <SimpleNotesModal
          title={`Void this core — $${voidingCore.core_amount_usd.toFixed(2)}`}
          description="Use when the core was misidentified, vendor wrote it off, or it's a false positive."
          confirmLabel="Void"
          variant="destructive"
          required
          onClose={() => setVoidingCore(null)}
          onConfirm={async (reason) => {
            await api.voidCoreCharge(voidingCore.id, reason);
            setVoidingCore(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function CoreRow({ core, onMarkReturned, onConfirmCredit, onVoid }) {
  const isOpen = core.status === 'PENDING_RETURN' || core.status === 'CORE_RETURNED';
  return (
    <li className="px-5 py-3 hover:bg-[var(--color-cream-100)] transition-colors">
      <div className="flex items-center gap-4">
        <div className="w-9 h-9 rounded-[var(--radius-sm)] bg-[var(--color-action-50)] flex items-center justify-center text-[var(--color-action-600)] shrink-0">
          <Coins size={16} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-[13px] font-semibold text-[var(--color-warm-900)]">{core.vendor_name || '— unknown vendor —'}</span>
            <Badge tone="cream" size="xs">{core.paid_via.replace('_', ' ')}</Badge>
            <Badge tone={STATUS_TONES[core.status]} size="xs" dot>
              {STATUS_LABELS[core.status]}
            </Badge>
          </div>
          <div className="text-[11px] text-[var(--color-warm-500)] flex items-center gap-2 flex-wrap">
            {core.part_name && <span className="truncate max-w-md">{core.part_name}</span>}
            {core.part_number && <span className="mono-nums">[{core.part_number}]</span>}
            {core.invoice_number && <span>· Invoice {core.invoice_number}</span>}
            {core.ro_number && <span>· RO {core.ro_number}</span>}
            {core.shop_nickname && <span>· {core.shop_nickname}</span>}
          </div>
          {core.credit_memo_reference && (
            <div className="text-[11px] text-[var(--color-success-600)] mt-1">
              Credit ref: <span className="mono-nums">{core.credit_memo_reference}</span>
            </div>
          )}
        </div>

        <div className="text-right shrink-0">
          <div className="text-[14px] font-bold text-[var(--color-warm-900)] tabular-nums">
            ${core.core_amount_usd.toFixed(2)}
          </div>
          <div className="text-[10px] text-[var(--color-warm-500)]">core deposit</div>
        </div>

        {isOpen && (
          <div className="flex items-center gap-2 shrink-0">
            {core.status === 'PENDING_RETURN' && (
              <Button variant="ghost" size="sm" leftIcon={<CheckCircle2 size={14} />} onClick={onMarkReturned}>
                Returned
              </Button>
            )}
            {core.status === 'CORE_RETURNED' && (
              <Button variant="accent" size="sm" leftIcon={<CheckCircle2 size={14} />} onClick={onConfirmCredit}>
                Credit received
              </Button>
            )}
            <Button variant="ghost" size="sm" leftIcon={<X size={14} />} onClick={onVoid} title="Void">
              Void
            </Button>
          </div>
        )}
      </div>
    </li>
  );
}

function SimpleNotesModal({ title, description, confirmLabel, variant = 'primary', required = false, onClose, onConfirm }) {
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  return (
    <Modal
      open={true}
      onClose={onClose}
      title={title}
      description={description}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant={variant}
            loading={submitting}
            disabled={required && !notes.trim()}
            onClick={async () => {
              setSubmitting(true);
              try { await onConfirm(notes); } finally { setSubmitting(false); }
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <Input
        label={required ? 'Reason' : 'Notes (optional)'}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        autoFocus
      />
    </Modal>
  );
}

function CreditModal({ core, onClose, onConfirmed }) {
  const [ref, setRef] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  return (
    <Modal
      open={true}
      onClose={onClose}
      title={`Confirm credit received — $${core.core_amount_usd.toFixed(2)}`}
      description={`Vendor: ${core.vendor_name || '?'} · Closes the loop on this core charge.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="accent"
            loading={submitting}
            onClick={async () => {
              setSubmitting(true);
              try { await onConfirmed(ref || null, notes || null); } finally { setSubmitting(false); }
            }}
          >
            Confirm credit received
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Credit memo / reference (optional)"
          value={ref}
          onChange={(e) => setRef(e.target.value)}
          autoFocus
          hint="e.g., credit memo number, statement line item, check #"
        />
        <Input
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
    </Modal>
  );
}
